import ApiService from "@/core/services/api.service";

/**
 * POST reports/financials/balance-sheet[/layout] — full report
 * POST reports/financials/balance-sheet/summary[/layout] — summary subset
 * Layout segment (horizontal, vertical, …) is display/routing only; body is always the same shape.
 *
 * Dates: omit both startDate and endDate to let the server use the active financial year.
 * If both are sent, the API uses them (terms such as YTD does not override them).
 * terms defaults to YTD when not set on the form.
 */

/**
 * @param {object} form — report filter form (start_date, end_date, terms, branches)
 * @returns {{ startDate?: string, endDate?: string, terms: string, branches?: number[] }}
 */
export function normalizeBranchesForApi(formBranches) {
	if (formBranches == null) return undefined;
	const arr = Array.isArray(formBranches) ? formBranches : [formBranches];
	if (arr.length === 0) return undefined;
	const ids = arr
		.map((b) => {
			if (b == null) return null;
			if (typeof b === "object" && b !== null && "id" in b) {
				const id = b.id;
				const n = Number(id);
				return Number.isFinite(n) ? n : id;
			}
			const n = Number(b);
			return Number.isFinite(n) ? n : b;
		})
		.filter((id) => id != null && id !== "");
	return ids.length ? ids : undefined;
}

export function buildBalanceSheetRequestBody(form) {
	const body = {};
	if (form?.start_date) body.startDate = form.start_date;
	if (form?.end_date) body.endDate = form.end_date;
	const terms =
		form?.terms != null && String(form.terms).trim() !== ""
			? form.terms
			: "YTD";
	body.terms = terms;
	const branches = normalizeBranchesForApi(form?.branches);
	if (branches !== undefined) body.branches = branches;
	return body;
}

/**
 * @param {{ summary: boolean, layout?: string }} opts — layout: horizontal | vertical | optional slug
 */
export function balanceSheetReportPath({ summary, layout }) {
	const base = "reports/financials/balance-sheet";
	const slug =
		layout != null && String(layout).trim() !== ""
			? `/${encodeURIComponent(String(layout).trim())}`
			: "";
	if (summary) {
		return `${base}/summary${slug}`;
	}
	return `${base}${slug}`;
}

/**
 * Shared POST for full vs summary; path includes optional layout only.
 */
export function postBalanceSheetReport({ summary, layout, form }) {
	const path = balanceSheetReportPath({ summary, layout });
	const body = buildBalanceSheetRequestBody(form);
	return ApiService.post(path, body);
}

/** Axios response → balance sheet payload (supports wrapped { data: { … } }). */
export function unwrapBalanceSheetPayload(response) {
	const d = response?.data;
	if (!d) return {};
	if (d.assets != null || d.liabilities != null || d.grant_total_assets != null) {
		return d;
	}
	if (
		d.data &&
		typeof d.data === "object" &&
		(d.data.assets != null ||
			d.data.liabilities != null ||
			d.data.grant_total_assets != null)
	) {
		return d.data;
	}
	return typeof d === "object" ? d : {};
}
